import { Typography } from '@chargeafter/ui';
import { Box, styled } from '@mui/material';
import { ButtonName } from '../constants';
import { MuiTrackerButton } from './TrackerButtonWrapper';
import { ConnectivityIcon, WarningIcon } from '../icons';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100vh',
  gap: theme.spacing(3),
  padding: theme.spacing(5),
}));

export enum ERROR_TYPE {
  SERVER_ERROR = 'serverError',
  UNAUTHORIZED = 'unauthorized',
}

interface ErrorFallbackProps {
  type: ERROR_TYPE;
  onButtonClick: () => void;
}

enum SERVER_ERROR_PROPERTIES {
  HEADER = 'Something \n went wrong',
  BUTTON_TEXT = 'Start Over',
  DESC = 'Please try again',
}

enum UNAUTHORIZED_PROPERTIES {
  HEADER = 'Not Authorized',
  BUTTON_TEXT = 'logout',
  DESC = 'Unauthorized access. Try a different account or reach out to your system administrator if the issue persists.',
}

const StyledConnectivityIcon = styled(ConnectivityIcon)(({ theme }) => ({
  width: '35vw',
  height: '35vh',
}));

const StyledWarningIconIcon = styled(WarningIcon)(({ theme }) => ({
  width: '35vw',
  height: '35vh',
}));

type HeaderWhiteSpace = 'pre-line' | 'nowrap';

interface ErrorScreen {
  type: ERROR_TYPE;
  header: string;
  graphic: JSX.Element;
  buttonText: string;
  desc?: string;
  buttonFullWidth?: boolean;
  buttonName: ButtonName;
  headerWhiteSpace: HeaderWhiteSpace;
}

const errorsMap: Record<ERROR_TYPE, ErrorScreen> = {
  [ERROR_TYPE.SERVER_ERROR]: {
    type: ERROR_TYPE.SERVER_ERROR,
    header: SERVER_ERROR_PROPERTIES.HEADER,
    graphic: <StyledConnectivityIcon />,
    buttonText: SERVER_ERROR_PROPERTIES.BUTTON_TEXT,
    desc: SERVER_ERROR_PROPERTIES.DESC,
    buttonName: ButtonName.StartOver,
    headerWhiteSpace: 'pre-line',
  },
  [ERROR_TYPE.UNAUTHORIZED]: {
    type: ERROR_TYPE.SERVER_ERROR,
    header: UNAUTHORIZED_PROPERTIES.HEADER,
    graphic: <StyledWarningIconIcon />,
    buttonText: UNAUTHORIZED_PROPERTIES.BUTTON_TEXT,
    desc: UNAUTHORIZED_PROPERTIES.DESC,
    buttonName: ButtonName.Logout,
    headerWhiteSpace: 'nowrap',
  },
};

const StyledMuiTrackerButton = styled(MuiTrackerButton)(({ theme }) => ({
  size: 'large',
  color: 'primary',
  textTransform: 'uppercase',
  alignSelf: 'end',
  margin: `auto 0 ${theme.spacing(4)} 0`,
}));

const StyledHeader = styled(Typography)<{ whiteSpace: HeaderWhiteSpace }>(
  ({ whiteSpace }) => ({
    paddingTop: '5vh',
    textShadow: 'none',
    fontSize: '6vw',
    whiteSpace: whiteSpace,
    lineHeight: '10vh',
  }),
);
const StyledDesc = styled(Typography)(() => ({
  width: '85vw',
}));

const StyledIconContainer = styled(Box)(() => ({
  display: 'flex',
  height: '100%',
  alignItems: 'center',
}));

export const ErrorFallback = ({ type, onButtonClick }: ErrorFallbackProps) => {
  return (
    <Container>
      <StyledHeader
        whiteSpace={errorsMap[type].headerWhiteSpace}
        component="h2"
        align="center"
        variant="main"
      >
        {errorsMap[type].header}
      </StyledHeader>
      <StyledDesc variant="h4" component="h4" align="center">
        {errorsMap[type].desc}
      </StyledDesc>
      <StyledIconContainer>{errorsMap[type].graphic}</StyledIconContainer>

      <StyledMuiTrackerButton
        buttonName={errorsMap[type].buttonName}
        fullWidth
        variant="contained"
        onClick={onButtonClick}
      >
        {errorsMap[type].buttonText}
      </StyledMuiTrackerButton>
    </Container>
  );
};
